/**
 * Mockup composite for the Act2 film strip (H-2026-09-13-137) — Canvas
 * composition of a transparent-PNG material onto a real blank-carrier photo
 * (SDS PDP blankDesignUrl lineage), replacing the synthetic SVG silhouette
 * tiles in the studio gallery.
 *
 * Print placement spec (calibrated against the SDS end-product photos in
 * docs/handoffs/studio-a2-mockup-film.html):
 *   - design width  = blank image width * 34%
 *   - design top    = blank image height * 26%
 *   - horizontally centered; the material aspect ratio is preserved
 *
 * The blank catalog is NOT hardcoded here: `loadBlanks()` reads
 * GET /api/blanks (worker route backed by KV `config:blank_carriers`) and
 * merges the remote rows over the local `BLANKS` metadata (labels + fallback
 * ratios). The chosen carrier persists for the session via module state
 * (getBlankKey/setBlankKey) — switching a chip recomposes every cell.
 *
 * Composition is Canvas-only; nothing is persisted (persistence belongs to
 * the post-pick upscale flow, outside this card).
 */

import { CONFIG } from '../../core/config.js'
import { apiRequest } from '../../api/index.js'
import { loadImage } from '../../utils/mockup-composite.js'

// Print placement spec (see header). Fractions of the blank image box.
export const PRINT_WIDTH_RATIO = 0.34
export const PRINT_TOP_RATIO = 0.26

// Output canvas cap — blank photos arrive at PDP resolution (e.g. 1350x1800);
// the film strip only needs thumb/big-preview sizes.
const CANVAS_MAX_WIDTH = 900

/**
 * Local carrier metadata. `blankUrl` is filled in by loadBlanks() from the
 * worker endpoint; `ratio` (width/height) is the fallback canvas aspect when
 * the blank photo is unavailable.
 */
export const BLANKS = [
  { key: 'black', label: 'Black Tee', ratio: 3 / 4 },
  { key: 'white', label: 'White Tee', ratio: 3 / 4 },
  { key: 'mug', label: 'Mug', ratio: 1 },
  { key: 'frame', label: 'Frame', ratio: 3 / 4 },
]

/** Cached blanks promise (session-scoped; one fetch per session). */
let _blanksPromise = null
/** Session-persistent current carrier key (survives Esc -> hero -> gallery). */
let _currentKey = 'black'

/**
 * Fetch + merge the blank catalog. Remote rows (key/label/blank_url/ratio)
 * override local metadata per key; endpoint failures degrade to the local
 * list with blankUrl null (composeMockup then renders the material alone).
 */
export function loadBlanks() {
  if (!_blanksPromise) {
    _blanksPromise = (async () => {
      let remote = []
      try {
        const res = await apiRequest(`${CONFIG.FRONTEND_API}/api/blanks`)
        remote = Array.isArray(res?.data?.blanks) ? res.data.blanks : []
      } catch {
        remote = []
      }
      return BLANKS.map((base) => {
        const hit = remote.find((r) => r && r.key === base.key)
        const ratio = Number(hit?.ratio)
        return {
          ...base,
          label: typeof hit?.label === 'string' && hit.label ? hit.label : base.label,
          blankUrl: typeof hit?.blank_url === 'string' && hit.blank_url ? hit.blank_url : null,
          ratio: ratio > 0 ? ratio : base.ratio,
          // 2026-11-02 one-click buy: the worker joins the governed product
          // (price/title) onto carriers with a product_id; buyable:false
          // means preview-only — the UI must not offer purchase on it.
          productId: typeof hit?.product_id === 'string' ? hit.product_id : null,
          buyable: hit?.buyable === true,
          priceUsdCents:
            typeof hit?.price_usd_cents === 'number' ? hit.price_usd_cents : null,
          productTitle: typeof hit?.title === 'string' ? hit.title : null,
        }
      })
    })()
  }
  return _blanksPromise
}

/** Test hook — drop the cached catalog so the next loadBlanks() refetches. */
export function clearBlanksCache() {
  _blanksPromise = null
}

export function getBlankKey() {
  return _currentKey
}

export function setBlankKey(key) {
  if (BLANKS.some((b) => b.key === key)) _currentKey = key
  return _currentKey
}

export async function getBlank(key = _currentKey) {
  const blanks = await loadBlanks()
  return blanks.find((b) => b.key === key) || blanks[0]
}

/** Active carrier's purchasable identity (null = not buyable). */
export async function getActiveBuyable() {
  const blank = await getBlank()
  if (!blank?.buyable || !blank.productId) return null
  return {
    productId: blank.productId,
    priceUsdCents: blank.priceUsdCents,
    label: blank.productTitle || blank.label,
  }
}

/**
 * Print box inside the blank image box, per the spec in the header.
 * @returns {{x: number, y: number, width: number, height: number}}
 */
export function computePrintPlacement(blankW, blankH, materialW, materialH) {
  const width = blankW * PRINT_WIDTH_RATIO
  const height = materialW > 0 ? width * (materialH / materialW) : width
  return { x: (blankW - width) / 2, y: blankH * PRINT_TOP_RATIO, width, height }
}

/**
 * In-place hue rotation over RGBA pixels (ported from the demo's shiftHue).
 * Fully/nearly transparent pixels are skipped so the material's alpha edges
 * stay clean on dark blanks.
 * @param {Uint8ClampedArray} data
 * @param {number} shift degrees, 0-360
 */
export function shiftHueData(data, shift) {
  for (let p = 0; p < data.length; p += 4) {
    if (data[p + 3] < 10) continue
    const r = data[p] / 255
    const g = data[p + 1] / 255
    const b = data[p + 2] / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min
    let h = 0
    const s = max === 0 ? 0 : delta / max
    const v = max
    if (delta !== 0) {
      if (max === r) h = ((g - b) / delta) % 6
      else if (max === g) h = (b - r) / delta + 2
      else h = (r - g) / delta + 4
    }
    h = (h * 60 + shift + 360) % 360
    const hh = h / 60
    const c = v * s
    const x = c * (1 - Math.abs((hh % 2) - 1))
    const m = v - c
    let rr, gg, bb
    if (hh < 1) [rr, gg, bb] = [c, x, 0]
    else if (hh < 2) [rr, gg, bb] = [x, c, 0]
    else if (hh < 3) [rr, gg, bb] = [0, c, x]
    else if (hh < 4) [rr, gg, bb] = [0, x, c]
    else if (hh < 5) [rr, gg, bb] = [x, 0, c]
    else [rr, gg, bb] = [c, 0, x]
    data[p] = (rr + m) * 255
    data[p + 1] = (gg + m) * 255
    data[p + 2] = (bb + m) * 255
  }
  return data
}

function imageDims(img) {
  const w = img?.naturalWidth || img?.width || 0
  const h = img?.naturalHeight || img?.height || 0
  return { w, h }
}

/**
 * Compose one mockup: blank photo full-bleed + the transparent material in
 * the print box. Returns a dataURL (BlobURL is unnecessary — no persistence).
 *
 * @param {string} materialUrl transparent-PNG material
 * @param {string|object} [blank] carrier key or a blank spec object
 * @param {{hue?: number}} [options] optional hue rotation (demo variant shift)
 * @returns {Promise<{key: string, dataUrl: string, blank: object}>}
 */
export async function composeMockup(materialUrl, blank = _currentKey, { hue = 0, cropToMaterial = false } = {}) {
  const spec = typeof blank === 'string' ? await getBlank(blank) : blank
  const material = await loadImage(materialUrl)
  const blankImg = spec?.blankUrl
    ? await loadImage(spec.blankUrl).catch(() => null)
    : null

  // Canvas box: the blank photo's own aspect when it loads, else the
  // carrier's declared ratio (width/height).
  const { w: bw, h: bh } = imageDims(blankImg)
  let W
  let H
  if (bw > 0 && bh > 0) {
    W = Math.min(bw, CANVAS_MAX_WIDTH)
    H = Math.round((W * bh) / bw)
  } else {
    W = CANVAS_MAX_WIDTH
    H = Math.round(W / (spec?.ratio || 3 / 4))
  }

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (blankImg) ctx.drawImage(blankImg, 0, 0, W, H)

  const { w: mw, h: mh } = imageDims(material)
  let source = material
  if (hue > 0 && mw > 0 && mh > 0) {
    // Hue variants go through an offscreen canvas so the material URL itself
    // is never mutated (the demo's per-cell hueMap trick).
    const off = document.createElement('canvas')
    off.width = mw
    off.height = mh
    const octx = off.getContext('2d')
    octx.drawImage(material, 0, 0)
    const pixels = octx.getImageData(0, 0, mw, mh)
    shiftHueData(pixels.data, hue)
    octx.putImageData(pixels, 0, 0)
    source = off
  }

  const box = computePrintPlacement(W, H, mw, mh)
  ctx.drawImage(source, box.x, box.y, box.width, box.height)

  // Thumb mode (intent strip): crop to the design area with a little padding
  // so the 成品图 reads at 64-113px — the full-body model photo swallows the
  // design at that size.
  if (cropToMaterial && box.width > 0 && box.height > 0) {
    const pad = Math.round(box.width * 0.15)
    const sx = Math.max(0, box.x - pad)
    const sy = Math.max(0, box.y - pad)
    const sw = Math.min(W - sx, box.width + pad * 2)
    const sh = Math.min(H - sy, box.height + pad * 2)
    const cropped = document.createElement('canvas')
    cropped.width = Math.round(sw)
    cropped.height = Math.round(sh)
    cropped.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh)
    return { key: spec?.key || 'black', dataUrl: cropped.toDataURL('image/png'), blank: spec }
  }

  return { key: spec?.key || 'black', dataUrl: canvas.toDataURL('image/png'), blank: spec }
}
